/* ==========================================================================
   LSC - Collage Collection: text carousel (created 15 Oct 2026, v3;
   v11 16 Oct 2026: started near the screen, one shared ResizeObserver;
   v19 19 Oct 2026: stickier swipes on phones, see SNAP SETTLE)
   assets/lsc-collage-collection-carousel.js

   Loaded by sections/lsc-collage-collection.liquid only when "Text
   carousel" is on.

   MARKUP IT EXPECTS (one per product row that has 2+ slides)
     [data-lsc-cc-carousel]       the text side
       [data-lsc-cc-track]        horizontal scroll-snap track
         [data-lsc-cc-slide] x N  one slide per view, 2nd+ start inert
       [data-lsc-cc-prev]         round "Previous" button (desktop)
       [data-lsc-cc-next] x 2     round "Next" button (desktop) and the edge
                                  arrow (phones); both just go forward
       [data-lsc-cc-dot="i"] x N  dots (phones)

   WHAT IT DOES
   - Buttons and dots scroll the track to a slide. Next on the last slide
     goes back to the first, Previous on the first goes to the last (loop).
   - Swiping is native scroll-snap; the script follows the scroll position
     to keep the dots and the inert state in step.
   - Slides that aren't showing get the `inert` attribute, so keyboard and
     screen-reader users only meet the visible slide. Swiping still works,
     because the scroll belongs to the track, not to the slides.
   - Visitors with "reduce motion" on get instant jumps instead of smooth
     scrolling.
   - After a resize the track re-snaps to the current slide (slide widths
     change with the viewport).
   - Phones only (under 768px): the track's height follows the visible
     slide, so a short slide doesn't leave a gap above the dots. Desktop
     keeps the tallest slide's height, so the row never shifts while
     clicking through.

   RE-RENDERS
   The theme editor re-renders sections without re-running scripts, and the
   theme's filter drawer swaps the rows after each filter change, firing
   "collection:reloaded". Both trigger a re-scan; each carousel is set up
   once (marked with a property on the element).

   VIEW TOGGLE (v6)
   The collage view can be hidden by the Collage / List toggle. A hidden
   carousel measures zero, so fit() leaves its height alone, and every
   carousel re-snaps on "lsc-cc:viewchange" (fired by
   assets/lsc-collage-collection-view.js) once it is visible again.
   A ResizeObserver on the slides also re-fits the phone height whenever a
   slide's size changes later (web fonts arriving, images loading).

   LSC - v11 (16 Oct 2026, audit fix 4): cheaper at load and on resize.
   - Each carousel is set up only when it comes within 300px of the screen
     (one IntersectionObserver for all of them). v3-v10 set up all 24 at
     load, after every filter change and on view switches, and each set-up
     measures its slides, which forced layout of every row, including the
     off-screen rows the CSS now lets the browser skip
     (content-visibility). A carousel in a hidden view never "comes near",
     so it waits until its view shows. Until set up, a carousel simply
     shows its first slide (the 2nd+ are inert in the markup) at its
     natural height.
   - One ResizeObserver for all slides (v6-v10: one per carousel, never
     disconnected); slides of carousels that left the page are unobserved.
   - The window resize re-snap only runs when the WIDTH changed. Phones fire
     resize when the address bar slides in and out while scrolling; slide
     widths don't change then, so re-snapping all carousels was wasted.

   SNAP SETTLE (LSC - v19, 19 Oct 2026)
   Swiping on phones could overshoot or come to rest between two slides.
   Two parts fix it:
   - CSS: scroll-snap-stop: always on every slide
     (assets/lsc-collage-collection.css), so one swipe moves at most one
     slide, however fast the flick.
   - Here: once a swipe has finished (the "scrollend" event where the
     browser has it, otherwise 140ms after the last scroll event) and no
     finger is on the track, the track is moved to the nearest slide if it
     stopped more than 1px away from one. This covers browsers whose
     snapping is unreliable after a height change (phones resize the track
     to the visible slide) or a fast flick. It never runs while a finger is
     down or while a button-driven scroll is still under way, so it cannot
     fight the shopper.
   ========================================================================== */
(function () {
  // Loaded by a second instance of the section: just pick up new carousels.
  if (window.LscCcCarousel) {
    window.LscCcCarousel.scan();
    return;
  }

  var reduceMotion = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  // Same breakpoint as the CSS that swaps buttons for the edge arrow + dots.
  var phone = window.matchMedia ? window.matchMedia('(max-width: 767px)') : null;
  var instances = []; // set-up carousels, for the resize handler
  var pending = []; // v11: carousels waiting to come near the screen

  // LSC - v11: ONE ResizeObserver for every slide; each slide carries its
  // carousel's fit() (set in setup), so a size change re-fits only that
  // carousel. See setup() for why the re-fit is needed.
  var resizeObserver =
    'ResizeObserver' in window
      ? new ResizeObserver(function (entries) {
          entries.forEach(function (entry) {
            if (entry.target.__lscCcFit) entry.target.__lscCcFit();
          });
        })
      : null;

  // LSC - v11: start each carousel only when it is near the screen (see the
  // header). Without IntersectionObserver every carousel starts at once.
  var starter =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (!entry.isIntersecting) return;
              starter.unobserve(entry.target);
              var at = pending.indexOf(entry.target);
              if (at !== -1) pending.splice(at, 1);
              setup(entry.target);
            });
          },
          { rootMargin: '300px 0px' }
        )
      : null;

  function setup(root) {
    if (root.__lscCc) return; // already set up
    var track = root.querySelector('[data-lsc-cc-track]');
    var slides = Array.prototype.slice.call(root.querySelectorAll('[data-lsc-cc-slide]'));
    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-lsc-cc-dot]'));
    if (!track || slides.length < 2) return;

    var count = slides.length;
    var current = 0;
    var queued = false;
    var lockUntil = 0; // ignore scroll-follow while a button-driven scroll runs

    /** Scroll offset of slide i, measured from the first slide. */
    function offsetOf(i) {
      return slides[i].offsetLeft - slides[0].offsetLeft;
    }

    /** Phones: track height = visible slide's height. Desktop: natural.
        LSC - v6: a carousel in a hidden view measures 0; leave the height
        unset then (it is fitted on "lsc-cc:viewchange" once it shows). */
    function fit() {
      var height = slides[current].offsetHeight;
      if (phone && phone.matches && height) {
        track.style.height = height + 'px';
      } else {
        track.style.removeProperty('height');
      }
    }

    /** Mark slide i as the visible one: dots + inert + phone height. */
    function setCurrent(i) {
      current = i;
      slides.forEach(function (slide, k) {
        if (k === i) slide.removeAttribute('inert');
        else slide.setAttribute('inert', '');
      });
      dots.forEach(function (dot, k) {
        if (k === i) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      fit();
    }

    /** Go to slide i, wrapping past either end. */
    function go(i, instant) {
      i = ((i % count) + count) % count;
      var smooth = !instant && !(reduceMotion && reduceMotion.matches);
      lockUntil = smooth ? Date.now() + 700 : 0;
      track.scrollTo({ left: offsetOf(i), behavior: smooth ? 'smooth' : 'auto' });
      setCurrent(i);
    }

    /** Follow swipes: the slide nearest the scroll position is current. */
    function follow() {
      queued = false;
      if (Date.now() < lockUntil) return;
      var step = offsetOf(1) || 1;
      var i = Math.round(track.scrollLeft / step);
      i = Math.max(0, Math.min(count - 1, i));
      if (i !== current) setCurrent(i);
    }

    // LSC - v19: SNAP SETTLE (see the header). touching = a finger is on
    // the track; settleTimer = the fallback for browsers without scrollend.
    var touching = false;
    var settleTimer = null;

    /** Move the track onto the nearest slide if a swipe left it between
        two, and make that slide the current one. */
    function settle() {
      clearTimeout(settleTimer);
      if (touching || Date.now() < lockUntil) return;
      var step = offsetOf(1) || 1;
      var i = Math.max(0, Math.min(count - 1, Math.round(track.scrollLeft / step)));
      var target = offsetOf(i);
      if (Math.abs(track.scrollLeft - target) > 1) {
        var smooth = !(reduceMotion && reduceMotion.matches);
        track.scrollTo({ left: target, behavior: smooth ? 'smooth' : 'auto' });
      }
      if (i !== current) setCurrent(i);
    }

    /** Fallback timing: settle once scrolling has been quiet for 140ms. */
    function scheduleSettle() {
      clearTimeout(settleTimer);
      settleTimer = setTimeout(settle, 140);
    }

    track.addEventListener(
      'touchstart',
      function () {
        touching = true;
        clearTimeout(settleTimer);
      },
      { passive: true }
    );
    ['touchend', 'touchcancel'].forEach(function (type) {
      track.addEventListener(
        type,
        function () {
          touching = false;
          scheduleSettle(); // momentum may follow; scroll events reschedule it
        },
        { passive: true }
      );
    });
    if ('onscrollend' in window) track.addEventListener('scrollend', settle);

    track.addEventListener(
      'scroll',
      function () {
        scheduleSettle(); // v19: also the fallback for browsers without scrollend
        if (queued) return;
        queued = true;
        window.requestAnimationFrame(follow);
      },
      { passive: true }
    );

    // One click listener for every control in this carousel.
    root.addEventListener('click', function (event) {
      var target = event.target.closest('[data-lsc-cc-prev], [data-lsc-cc-next], [data-lsc-cc-dot]');
      if (!target || !root.contains(target)) return;
      if (target.hasAttribute('data-lsc-cc-prev')) go(current - 1);
      else if (target.hasAttribute('data-lsc-cc-next')) go(current + 1);
      else go(parseInt(target.getAttribute('data-lsc-cc-dot'), 10) || 0);
    });

    // LSC - v6: re-fit the phone height whenever a slide changes size after
    // it was measured. Found when switching from the list view: the quote's
    // italic web font is only downloaded once the collage view shows, so the
    // text first lays out in a fallback font and then grows (26px on
    // GlyMed), which clipped the bottom of the quote. Late images and font
    // swaps in general are covered the same way. Setting the track height
    // never changes a slide's own height, so this cannot loop.
    // LSC - v11: through the shared observer (see the top of the file).
    if (resizeObserver) {
      slides.forEach(function (slide) {
        slide.__lscCcFit = fit;
        resizeObserver.observe(slide);
      });
    }

    root.__lscCc = { resnap: function () { go(current, true); }, slides: slides };
    instances.push(root);
    setCurrent(0);
  }

  /** Queue new carousels to start near the screen (v11) and forget ones
      that left the page (filter change, theme editor). */
  function scan() {
    instances = instances.filter(function (root) {
      if (root.isConnected) return true;
      // v11: stop watching its slides, so they can be freed.
      if (resizeObserver && root.__lscCc) {
        root.__lscCc.slides.forEach(function (slide) {
          resizeObserver.unobserve(slide);
        });
      }
      return false;
    });
    pending = pending.filter(function (root) {
      if (root.isConnected) return true;
      if (starter) starter.unobserve(root);
      return false;
    });
    document.querySelectorAll('[data-lsc-cc-carousel]').forEach(function (root) {
      if (root.__lscCc || pending.indexOf(root) !== -1) return; // set up or queued
      if (!starter) {
        setup(root);
        return;
      }
      pending.push(root);
      starter.observe(root);
    });
  }

  // Re-snap after resizes settle (slide widths follow the viewport). This
  // also re-fits the phone height, since go() calls setCurrent().
  // LSC - v11: only when the width changed (see the header).
  var resizeTimer = null;
  var lastWidth = window.innerWidth;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      if (window.innerWidth === lastWidth) return; // height only: phone address bar
      lastWidth = window.innerWidth;
      instances.forEach(function (root) {
        if (root.__lscCc) root.__lscCc.resnap();
      });
    }, 150);
  });

  document.addEventListener('shopify:section:load', scan);
  document.addEventListener('collection:reloaded', scan);

  // LSC - v6: the view toggle (assets/lsc-collage-collection-view.js) just
  // showed or hid the collage view. Carousels set up while hidden measured
  // nothing, so re-snap and re-fit them all now that they can be measured.
  document.addEventListener('lsc-cc:viewchange', function () {
    instances.forEach(function (root) {
      if (root.isConnected && root.__lscCc) root.__lscCc.resnap();
    });
  });

  window.LscCcCarousel = { scan: scan };
  scan();
})();
